import { DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
	Button,
	Card,
	Col,
	Divider,
	Flex,
	Form,
	Image,
	Input,
	message,
	notification,
	Row,
	Select,
	Space,
	Tabs,
	Upload,
	Typography,
} from 'antd';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import uploadApi from '../../api/file';
import productApi from '../../api/product';
import SH_Model from '../../components/SH_Model';
import Constant from '../../lib/constant';

const { Text } = Typography;

const defaultData = {
	names: { vi: '', en: '' },
	createdAt: new Date(),
	descriptions: { vi: '', en: '' },
	contents: { vi: '', en: '' },
	categories: [],
	images: [],
};

function TV_product_detail(props, ref) {
	const { getProducts, categories, projects } = props;

	const [open, setOpen] = useState(false);
	const [index, setIndex] = useState(defaultData);
	const [initValue, setInitValue] = useState(defaultData);

	const refMode = useRef('');
	const [api, contextHolder] = notification.useNotification();

	const [fileList, setFileList] = useState([]);
	const [uploading, setUploading] = useState(false);
	const [images, setImages] = useState([]);
	const [loading, setLoading] = useState(false);

	const [errors, setErrors] = useState({});
	const propsErrors = {
		namesvi: null,
		namesen: null,
		contentsvi: null,
		contentsen: null,
		descriptionsvi: null,
		descriptionsen: null,
		categories: null,
		images: null,
		projectId: null,
	};

	const [form] = Form.useForm();

	const openNotificationWithIcon = (type, _message, description) => {
		api[type]({ message: _message, description });
	};

	useImperativeHandle(ref, () => ({
		create: () => {
			setImages([]);
			setFileList([]);
			setErrors(propsErrors);
			setInitValue(defaultData);
			setIndex(defaultData);
			setOpen(true);
			refMode.current = Constant.crud.create;
			form.resetFields();
		},
		update: (product) => {
			const { contents, ...remain } = product;
			setInitValue({ ...remain, contents: contents ? contents : { vi: '', en: '' } });
			setImages(product.images || []);
			setIndex({ ...product, contents: contents ? contents : { vi: '', en: '' } });
			setErrors(propsErrors);
			setFileList([]);
			setOpen(true);
			refMode.current = Constant.crud.update;
			form.resetFields();
		},
	}));

	const validate = (propsValidate, data) => {
		const _data = structuredClone(
			data || { ...initValue, contents: index.contents, descriptions: index.descriptions, images }
		);

		const _errors = structuredClone(propsErrors);
		const propsToCheck = propsValidate || propsErrors;

		Object.keys(propsToCheck).forEach((prop) => {
			switch (prop) {
				case 'contentsvi':
					_errors[prop] = _data?.contents?.vi?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				case 'contentsen':
					_errors[prop] = _data?.contents?.en?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				case 'descriptionsvi':
					_errors[prop] = _data?.descriptions?.vi?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				case 'descriptionsen':
					_errors[prop] = _data?.descriptions?.en?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				case 'images':
					_errors[prop] = images?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				case 'namesvi':
					_errors[prop] = _data?.names?.vi?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				case 'namesen':
					_errors[prop] = _data?.names?.en?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
				default:
					_errors[prop] = _data?.[prop]?.length > 0 ? null : 'Dữ liệu không thể để trống';
					break;
			}
		});

		setErrors(_errors);
		return Object.values(_errors);
	};

	const handleSubmit = async () => {
		setInitValue({ ...initValue, contents: index.contents, descriptions: index.descriptions, images });

		try {
			const _errorList = validate();
			if (_errorList.filter((x) => x).length !== 0) return;

			setLoading(true);

			if (refMode.current === Constant.crud.update) {
				const { _id, names, categories: _categories, projectId } = initValue;

				await productApi.update(_id, {
					names,
					images: images?.map((img) => ({
						url: img.url,
						signature: img.signature,
						public_id: img.public_id,
					})),
					projectId,
					categories: _categories,
					contents: index.contents,
					descriptions: index.descriptions,
				});

				openNotificationWithIcon('success', 'Thành công', 'Thao tác thành công');
			} else {
				await productApi.create({
					...initValue,
					images,
					contents: index.contents,
					descriptions: index.descriptions,
				});

				openNotificationWithIcon('success', 'Thành công', 'Thao tác thành công');
			}

			onCancel();
			getProducts();
		} catch (err) {
			// giữ logic hiện tại: chỉ log
			// eslint-disable-next-line no-console
			console.log(err);
		} finally {
			setLoading(false);
		}
	};

	const handleRemoveImage = async (img) => {
		setImages(images.filter((_img) => _img.public_id !== img.public_id));
	};

	const onCancel = () => {
		setOpen(false);
		refMode.current = '';
		form.resetFields();
		setInitValue(defaultData);
		setIndex(defaultData);
		setErrors(propsErrors);
		setFileList([]);
		setImages([]);
	};

	function uploadPlugin(editor) {
		editor.plugins.get('FileRepository').createUploadAdapter = (loader) => uploadAdapter(loader);
	}

	function uploadAdapter(loader) {
		return {
			upload: () =>
				new Promise(async (resolve, reject) => {
					try {
						const file = await loader.file;
						if (file) {
							uploadApi
								.uploadImage(file)
								.then((data) => {
									resolve({ default: data.data.data.url });
									message.success('upload successfully.');
								})
								.catch(() => {
									message.error('upload failed.');
								});
						}
					} catch (error) {
						reject('Error');
					}
				}),
			abort: () => {},
		};
	}

	const handleUpload = async () => {
		const apis = [];
		for (let i = 0; i < fileList.length; i++) {
			apis.push(uploadApi.uploadImage(fileList[i]));
		}
		setUploading(true);
		try {
			const response = await Promise.all(apis);
			response.forEach((res) => setImages((prev) => [...prev, res.data.data]));
			message.success('upload successfully.');
		} finally {
			setUploading(false);
		}
	};

	const uploadProps = {
		onRemove: (file) => {
			const idx = fileList.indexOf(file);
			const newFileList = fileList.slice();
			newFileList.splice(idx, 1);
			setFileList(newFileList);
		},
		beforeUpload: (file) => {
			setFileList((prev) => [...prev, file]);
			return false;
		},
		fileList,
		multiple: true,
	};

	const isCreate = refMode.current === Constant.crud.create;

	return (
		<>
			{contextHolder}

			<SH_Model
				open={open}
				width="80%"
				handleSubmit={handleSubmit}
				onCancel={onCancel}
				textOk={isCreate ? 'Tạo mới' : 'Cập nhật'}
				loading={loading}
				title={isCreate ? 'Tạo mới sản phẩm' : 'Cập nhật sản phẩm'}
				heights="75vh"
			>
				<Form form={form} layout="vertical" requiredMark={false}>
					<Card size="small" title="Thông tin cơ bản" style={{ borderRadius: 12 }}>
						<Row gutter={16}>
							<Col span={12}>
								<Form.Item label="Tên sản phẩm (Tiếng Việt)" required>
									<Input
										placeholder="Nhập tên sản phẩm (VI)"
										value={initValue.names?.vi}
										onChange={(e) =>
											setInitValue((prev) => ({
												...prev,
												names: { vi: e.target.value, en: prev.names.en },
											}))
										}
									/>
									<Text type="danger">{errors['namesvi']}</Text>
								</Form.Item>
							</Col>

							<Col span={12}>
								<Form.Item label="Tên sản phẩm (Tiếng Anh)" required>
									<Input
										placeholder="Enter product name (EN)"
										value={initValue.names?.en}
										onChange={(e) =>
											setInitValue((prev) => ({
												...prev,
												names: { en: e.target.value, vi: prev.names.vi },
											}))
										}
									/>
									<Text type="danger">{errors['namesen']}</Text>
								</Form.Item>
							</Col>
						</Row>

						<Row gutter={16}>
							<Col span={12}>
								<Form.Item label="Dự án">
									<Select
										allowClear
										style={{ width: '100%' }}
										fieldNames={{ label: 'label', value: '_id' }}
										value={initValue.projectId}
										options={projects}
										placeholder="Chọn dự án"
										onChange={(e) => setInitValue((prev) => ({ ...prev, projectId: e }))}
									/>
									<Text type="danger">{errors['projectId']}</Text>
								</Form.Item>
							</Col>

							<Col span={12}>
								<Form.Item label="Loại sản phẩm" required>
									<Select
										mode="multiple"
										allowClear
										style={{ width: '100%' }}
										fieldNames={{ label: 'label', value: '_id' }}
										value={initValue.categories}
										options={categories}
										placeholder="Chọn loại sản phẩm"
										onChange={(e) => setInitValue((prev) => ({ ...prev, categories: e }))}
									/>
									<Text type="danger">{errors['categories']}</Text>
								</Form.Item>
							</Col>
						</Row>
					</Card>

					<Divider style={{ margin: '14px 0' }} />

					<Card size="small" title="Nội dung hiển thị" style={{ borderRadius: 12 }}>
						<Tabs
							defaultActiveKey="vi"
							destroyInactiveTabPane={false}
							items={[
								{
									key: 'vi',
									label: 'Tiếng Việt',
									children: (
										<Space direction="vertical" size={14} style={{ width: '100%' }}>
											<div>
												<div style={{ fontWeight: 600, marginBottom: 8 }}>Mô tả (VI)</div>
												<CKEditor
													editor={ClassicEditor}
													data={index?.descriptions?.vi}
													config={{
														extraPlugins: [uploadPlugin],
														mediaEmbed: { previewsInData: true },
													}}
													onChange={(event, editor) => {
														const data = editor.getData();
														setIndex((prev) => ({
															...prev,
															descriptions: { vi: data, en: prev.descriptions.en },
														}));
													}}
												/>
												<Text type="danger">{errors['descriptionsvi']}</Text>
											</div>

											<div>
												<div style={{ fontWeight: 600, marginBottom: 8 }}>Nội dung (VI)</div>
												<CKEditor
													editor={ClassicEditor}
													data={index?.contents?.vi}
													config={{
														extraPlugins: [uploadPlugin],
														mediaEmbed: { previewsInData: true },
													}}
													onChange={(event, editor) => {
														const data = editor.getData();
														setIndex((prev) => ({
															...prev,
															contents: { vi: data, en: prev.contents.en },
														}));
													}}
												/>
												<Text type="danger">{errors['contentsvi']}</Text>
											</div>
										</Space>
									),
								},
								{
									key: 'en',
									label: 'English',
									children: (
										<Space direction="vertical" size={14} style={{ width: '100%' }}>
											<div>
												<div style={{ fontWeight: 600, marginBottom: 8 }}>Description (EN)</div>
												<CKEditor
													editor={ClassicEditor}
													data={index?.descriptions?.en}
													config={{
														extraPlugins: [uploadPlugin],
														mediaEmbed: { previewsInData: true },
													}}
													onChange={(event, editor) => {
														const data = editor.getData();
														setIndex((prev) => ({
															...prev,
															descriptions: { en: data, vi: prev.descriptions.vi },
														}));
													}}
												/>
												<Text type="danger">{errors['descriptionsen']}</Text>
											</div>

											<div>
												<div style={{ fontWeight: 600, marginBottom: 8 }}>Content (EN)</div>
												<CKEditor
													editor={ClassicEditor}
													data={index?.contents?.en}
													config={{
														extraPlugins: [uploadPlugin],
														mediaEmbed: { previewsInData: true },
													}}
													onChange={(event, editor) => {
														const data = editor.getData();
														setIndex((prev) => ({
															...prev,
															contents: { en: data, vi: prev.contents.vi },
														}));
													}}
												/>
												<Text type="danger">{errors['contentsen']}</Text>
											</div>
										</Space>
									),
								},
							]}
						/>
					</Card>

					<Divider style={{ margin: '14px 0' }} />

					<Card size="small" title="Hình ảnh" style={{ borderRadius: 12 }}>
						<Space direction="vertical" size={10} style={{ width: '100%' }}>
							<Flex align="center" justify="space-between" wrap="wrap" gap={10}>
								<Space>
									<Upload {...uploadProps} listType="picture">
										<Button icon={<UploadOutlined />}>Chọn ảnh</Button>
									</Upload>

									<Button
										type="primary"
										onClick={handleUpload}
										disabled={fileList.length === 0}
										loading={uploading}
									>
										{uploading ? 'Đang tải' : 'Tải lên'}
									</Button>
								</Space>

								<Text type="secondary">
									Đã tải: <b>{images?.length || 0}</b> ảnh
								</Text>
							</Flex>

							<Text type="danger">{errors['images']}</Text>

							<Flex align="flex-start" wrap="wrap" gap={10}>
								<Image.PreviewGroup>
									{images.map((img, idx) => (
										<div
											key={`${img?.public_id || idx}`}
											style={{
												position: 'relative',
												borderRadius: 12,
												overflow: 'hidden',
												border: '1px solid #f0f0f0',
											}}
										>
											<Image
												width={120}
												height={90}
												style={{ objectFit: 'cover' }}
												src={img.url}
											/>
											<Button
												danger
												type="primary"
												size="small"
												style={{
													position: 'absolute',
													top: 6,
													right: 6,
													borderRadius: 999,
												}}
												onClick={() => handleRemoveImage(img)}
												icon={<DeleteOutlined />}
											/>
										</div>
									))}
								</Image.PreviewGroup>
							</Flex>
						</Space>
					</Card>
				</Form>
			</SH_Model>
		</>
	);
}

export default forwardRef(TV_product_detail);
